/* eslint-disable complexity */
import cn from 'classnames';
import {
  MultiTurnInteractionConfiguration,
  ModelResponseSelectorStepParams,
} from '@scale/shared/types/chat';
import { doesUrlSupportAttachments } from '@scale/shared/types/chat-completion';
import { useMemo } from 'react';
import { QMProject } from '@scale/shared/qm/qmRequestTypes';
import MLQualitySettings from 'react-scale/components/scalers/tasks/ThreadedInteraction/ChatConfigurationEditor/components/MLQualitySettings';
import useUnleashFeatureFlag from 'react-scale/hooks/useUnleashFeatureFlag';
import PromptOptions from './sections/PromptOptions';
import CodingOptions from './sections/CodingOptions';
import MultimodalOptions from './sections/MultimodalOptions';
import PreloadPromptsAndResponses from './sections/PreloadPromptsAndResponses';
import BeforeSteps from './sections/BeforeSteps';
import PerTurnSteps from './sections/PerTurnSteps';
import AfterSteps from './sections/AfterSteps';
import NumberOfTurns from './sections/NumberOfTurns';
import ChatHistory from './sections/ChatHistory';
import Specializations from './sections/Specializations';
import { withErrorBoundary } from 'react-scale/containers/global/errorBoundary';
import { Button, Typography } from '@scale/scaleui';
import { IChatTaskFeatureConfig } from '@scale/shared/types/config';

export type ChatConfigurationEditorProps = {
  value: MultiTurnInteractionConfiguration;
  onSubmit: (config: MultiTurnInteractionConfiguration) => void;
  project?: QMProject;
  chatTaskFeatureConfig?: IChatTaskFeatureConfig;
};

const ChatConfigurationEditor = ({
  value,
  onSubmit,
  project,
  chatTaskFeatureConfig,
}: ChatConfigurationEditorProps) => {
  const parseTurnsAmount = (amount: string) => {
    const parsedNumber = parseInt(amount);
    if (!isNaN(parsedNumber)) {
      return parsedNumber;
    }
    // this "{{placeholder}}" will eventually be parsed into a number by the api
    return amount;
  };

  const showEnableAttachmentsSwitch = useMemo(() => {
    if (!value.turn || value.turn.length < 1) {
      // If there is a PromptInput step, we should show the switch since attachments can be added in the prompt
      if (value.before?.find(step => step.type === 'PromptInput')) return true;
      return false;
    }
    const modelResponseSelectorSteps = value.turn.filter(
      step => step.type === 'ModelResponseSelector',
    );
    // If there are no ModelResponseSelector steps, show the switch since there is no model endpoint call and attachments might still be added in PromptInput steps
    if (!modelResponseSelectorSteps.length) return true;
    for (const step of modelResponseSelectorSteps) {
      const responseSelectorStep = step as ModelResponseSelectorStepParams;
      if (responseSelectorStep.params && responseSelectorStep.params.chat_models) {
        // if any of the chat_models has urlSupportAttachments = true, then show the switch
        for (const chatModel of responseSelectorStep.params.chat_models) {
          if (doesUrlSupportAttachments(chatModel.url)) {
            return true;
          }
        }
      }
    }
    return false;
  }, [value.turn, value.before]);

  const displayMLQualitySettings = useUnleashFeatureFlag(
    'use-ml-quality-settings',
    { projectId: project?.id },
    false,
  );

  return (
    <div className={cn('flex flex-col gap-4 text-neutral-900')}>
      <PromptOptions
        value={value}
        onSubmit={onSubmit}
        showEnableAttachmentsSwitch={showEnableAttachmentsSwitch}
        projectId={project?.id}
      />

      <CodingOptions
        value={value}
        onSubmit={onSubmit}
        showEnableAttachmentsSwitch={showEnableAttachmentsSwitch}
      />

      <MultimodalOptions value={value} onSubmit={onSubmit} />

      <PreloadPromptsAndResponses value={value} onSubmit={onSubmit} />

      <BeforeSteps
        value={value}
        onSubmit={onSubmit}
        displayMLQualitySettings={displayMLQualitySettings}
        chatTaskFeatureConfig={chatTaskFeatureConfig}
      />

      <PerTurnSteps
        value={value}
        onSubmit={onSubmit}
        displayMLQualitySettings={displayMLQualitySettings}
        chatTaskFeatureConfig={chatTaskFeatureConfig}
      />

      <AfterSteps
        value={value}
        onSubmit={onSubmit}
        displayMLQualitySettings={displayMLQualitySettings}
        chatTaskFeatureConfig={chatTaskFeatureConfig}
      />

      <NumberOfTurns value={value} onSubmit={onSubmit} parseTurnsAmount={parseTurnsAmount} />

      {displayMLQualitySettings && (
        <div>
          <MLQualitySettings name="ML Quality Settings" value={value} />
        </div>
      )}

      <ChatHistory value={value} onSubmit={onSubmit} />

      <Specializations value={value} onSubmit={onSubmit} />
    </div>
  );
};

function ErrorComponent({
  errorMessage,
  onReload,
}: {
  errorMessage: string | null;
  onReload: () => void;
}) {
  return (
    <div>
      <Typography.Paragraph2 className="text-danger-800">
        It looks like your taxonomy is invalid. Please fix the error in the JSON tab.
      </Typography.Paragraph2>
      <Typography.Paragraph2>Error message: {errorMessage}</Typography.Paragraph2>

      <Button variant="info" size="sm" onClick={onReload}>
        Reload (please do this after fixing the issue in JSON)
      </Button>
    </div>
  );
}

export default withErrorBoundary({ reloadable: true, ErrorPlaceholderComponent: ErrorComponent })(
  ChatConfigurationEditor,
);
